import { useAppInboxToasts, type AppInboxToastStateItem } from '@src/components/GlobalToasts/AppInboxToast';
import { EMPTY_OBJECT, isCypressWindow } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { isCypressE2E } from '../../../../../packages/kyoko/src/utils/envCypress';
import { useAppStateDispatch, useAppStateSelector } from '../AppStateProvider';
import { useGetExistingNotificationsQuery, useGetStreamingNotificationsQuery } from './redux/AppNotificationsApi';
import { progressNotificationState } from './redux/AppNotificationsSliceActions';

export const AppNotificationsMonitor = () => {
  /** Temporary setup until UserApi RTK Query setup is done */
  const user = useAppStateSelector(state => state.userInfo.user);
  const dispatch = useAppStateDispatch();
  const isTestEnvironment = typeof window !== 'undefined' && isCypressWindow(window) && !isCypressE2E(window);

  const { add, remove } = useAppInboxToasts();

  // Track when streaming query starts to only show toasts for new notifications
  const streamingQueryStarted = useRef<number | null>(null);

  // Kick off notifications queries - processing into redux state happens in the slice reducer
  // All of these results will be processed directly to the inbox, since these are previously existing notifications
  const { data: restNotifications } = useGetExistingNotificationsQuery(undefined, {
    skip: !user,
  });

  // start WS query once initial rest one is handled
  const { data: streamingNotifications } = useGetStreamingNotificationsQuery(EMPTY_OBJECT, {
    skip: !user || !restNotifications,
  });

  // Track when streaming query starts
  useEffect(() => {
    if (streamingNotifications && !streamingQueryStarted.current) {
      streamingQueryStarted.current = Date.now();
    }
  }, [streamingNotifications]);

  const toastsInUse = useRef(new Set<string>());
  const appNotificationState = useAppStateSelector(state => state.appNotifications);

  useEffect(() => {
    const newToasts = compact(
      Object.values(appNotificationState.notificationMap).filter(notification => notification?.state === 'new')
    );
    const removedToastsId = compact(
      Object.values(appNotificationState.notificationMap)
        .filter(appNotification => appNotification?.state !== 'new')
        .map(item => item?.notification.NotificationID)
    );

    // Only process new toasts that aren't already in use AND arrived after streaming query started
    const newToastsToAdd = newToasts.filter(({ toastInfo, notification }) => {
      // Skip if already in use
      if (toastsInUse.current.has(toastInfo.id)) {
        return false;
      }

      // This time checking logic doesn't work in cypress component test environments, so assume true
      if (isTestEnvironment) {
        return true;
      }

      // Only show toasts for notifications that arrived after streaming query started
      if (streamingQueryStarted.current) {
        const notificationTime = new Date(notification.Timestamp).getTime();
        return notificationTime >= streamingQueryStarted.current;
      }

      // If streaming hasn't started yet, don't show toasts in production
      return false;
    });

    newToastsToAdd.forEach(({ toastInfo, notification }) => {
      const toastInfoToSend: AppInboxToastStateItem = {
        ...toastInfo,
        timeout: toastInfo.variant === 'Low' ? 5_000 : 15_000,
      };
      // in-app Toast
      add(toastInfoToSend, notificationId => {
        dispatch(
          progressNotificationState({
            NotificationID: notificationId,
            state: 'inbox',
          })
        );
      });

      toastsInUse.current.add(toastInfo.id);
    });

    // Only remove toasts that are actually in use and should be removed
    const withdrawnToastIds = Array.from(toastsInUse.current).filter(toastId => removedToastsId.includes(toastId));
    withdrawnToastIds.forEach(toastId => {
      remove(toastId);
      toastsInUse.current.delete(toastId);
    });
  }, [add, appNotificationState.notificationMap, dispatch, isTestEnvironment, remove]);

  return null;
};
